/**
 * Tiny adapters that lift the shared `ApiClient` (used by both the game
 * bundle and the host) into the narrow `BidProvider` / `RewardValidator`
 * interfaces the `HostShell` consumes.
 *
 * The host bridges into `/ads/bid` and `/ads/reward/validate` exactly the
 * same way the game side would, but the shell only needs the narrow
 * surface — these adapters keep that boundary explicit and let unit tests
 * inject fakes without dragging an ApiClient stub in.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Wraps `ApiClient.requestBids` into `BidProvider.requestBidTargeting`.
 *
 * The host knows the embedded game's `appId` + `publisher` from its trusted
 * per-origin registry (see `HostShell.registerGame`) — those are forwarded
 * into the bid request as the `appId` + `publisherId` fields. `gameId`
 * rides in the request for AY-side reporting attribution.
 *
 * Used by every host-rendered format that needs a bid: interstitial (slot
 * targeting), video_interstitial + rewarded_video (VAST cust_params).
 * `/ads/bid` returns the same response shape; only the downstream wiring
 * (GPT `setTargeting` vs `mergeVastCustParams`) differs.
 */
export function createHostBidProvider(api, getSessionId) {
    return {
        requestBidTargeting(input) {
            return __awaiter(this, void 0, void 0, function* () {
                const sessionId = getSessionId();
                if (!sessionId)
                    return { targeting: {} };
                const response = yield api.requestBids({
                    sessionId,
                    appId: input.gameId,
                    publisherId: input.publisherId,
                    format: input.format,
                    sizes: [input.viewport],
                    placement: input.placementName,
                    adUnit: input.adUnit,
                    gameId: input.gameId,
                    category: input.category,
                    source: 'sdk',
                    adContext: 'ON_SITE',
                });
                // `exp` / `expiresAt` ride through unchanged — only meaningful for
                // bid-ahead formats (video_interstitial / rewarded_video) where
                // the BidFreshnessGate consumes them. Banner / display interstitial
                // ignore them.
                if (!response.hasBid)
                    return { targeting: {} };
                return {
                    targeting: response.targeting,
                    exp: response.exp,
                    expiresAt: response.expiresAt,
                };
            });
        },
    };
}
/**
 * Wraps `ApiClient.validateReward` into `RewardValidator.validateReward`.
 *
 * The host's session id is captured by closure — when the session refreshes
 * the bootstrap re-creates the validator (cheap; the validator is stateless).
 */
export function createHostRewardValidator(api, getSessionId) {
    return {
        validateReward(rewardType, context) {
            return __awaiter(this, void 0, void 0, function* () {
                var _a;
                const sessionId = getSessionId();
                if (!sessionId) {
                    return { granted: false, amount: 0, reason: 'no_session' };
                }
                const v = yield api.validateReward(sessionId, rewardType, context);
                return {
                    granted: v.rewardGranted,
                    amount: (_a = v.amount) !== null && _a !== void 0 ? _a : 0,
                    reason: v.reason,
                };
            });
        },
    };
}
